<template>
  <div
    class="TransactionList">
    <CRUDList
      :canEdit="true"
      :canSave="true"
      :headers="headers"
      :showDate="true"
      :showTotal="true"
      :baseCrudKey="baseCrudKey"
      toolbarTitle="SA&I Transactions"
      @upsertItem="upsrtTransaction"
      @loadCRUDList="handleLoadCRUDList"
      :createNewItem="newItemDialog"
    >
    <template v-slot:[`editedItem`]="{}">
        <SaiMonthlyTransactionEditedItem
        :baseCrudKey="baseCrudKey"
        />
      </template>
    <template v-slot:[`additionalToolsAtRight`]="{}">
      <v-row>
        <v-col cols="5">
          <v-select id="sai-monthly-criteria-section"
            v-model="criteriaState.section"
            attach
            label="Section"
            item-text="description"
            item-value="name"
            :items="saiSections"
            :menu-props="{ top: true, offsetY: true }"
            @change="searchBySectionCriteria"
          >
          </v-select>
        </v-col>
      </v-row>
    </template>
    </CRUDList>
  </div>
</template>
<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import CRUDList from '../../../common/base/crud/CRUDList.vue';
import SaiMonthlyTransactionEditedItem from './SaiMonthlyTransactionEditedItem.vue';
import { amountToNumber, extractId } from '../../../../util/shared/vue-global';
import { headersByType } from './transaction.headers';
import { getIdFromList, toFiscalYear } from '../../../../util/shared/tmc-global';

const collectionSection = ['unapportionLastMonth', 'receipts', 'transferIn', 'transferOut', 'refundsPaymentCancelled', 'total', 'apportioned', 'balanceUnapportioned', 'link', 'saiSection'];
const bankSection = ['line', 'balPerBankStatement', 'investment', 'reconcilement', 'adjBankBalance', 'glBankBalance', 'difference', 'link', 'saiSection'];
const fundSection = ['line', 'balanceLastMonth', 'apportioned', 'transferIn', 'transferOut', 'disbursements', 'od', 'balance', 'link', 'saiSection'];
const odSection = ['balanceLastMonth', 'deposits', 'transferIn', 'transferOut', 'vouchersRegistered', 'balance', 'link', 'saiSection'];

export default {
  data: () => ({
    baseCrudKey: 'saiMonthlyTransaction',
    saiMonthlySummaryBaseCrudKey: 'saiMonthlySummary',
  }),
  created() {
  },
  components: {
    CRUDList,
    SaiMonthlyTransactionEditedItem,
  },
  computed: {
    ...mapGetters(['todaysDate']),
    ...mapState({
      headers: (state) => state.glDaily.saiMonthlyTransaction.headers,
      SaiMonthlyTransactionSectionTypes: (state) => state.enums.SaiMonthlyTransactionSectionEnum,
    }),
    ...mapGetters('base/crud', [
      'criteria',
      'index',
      'item',
      'items',
    ]),
    /*
      monthlyTransaction criteriaState = {section}
      saiSummary criteriaState = {FY, Year, Month}
    */
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    saiMonthlySummaryCriteriaState: {
      get() {
        return this.criteria(this.saiMonthlySummaryBaseCrudKey);
      },
      set(value) {
        this.setCriteria([this.saiMonthlySummaryBaseCrudKey, value]);
      },
    },
    saiSections() {
      if (!this.SaiMonthlyTransactionSectionTypes) {
        return [];
      }
      return this.SaiMonthlyTransactionSectionTypes;
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setCriteria',
    ]),
    ...mapMutations('glDaily/saiMonthlyTransaction', [
      'setHeaders',
    ]),
    ...mapActions('glDaily/saiMonthlyTransaction', [
      'loadSaiMonthlyTransaction',
      'upsertSaiMonthlyTransaction',
    ]),
    saveableEdits(item) {
      const updatedItem = { ...item };
      if (updatedItem.section === 'BANKS') {
        bankSection.forEach((f) => {
          updatedItem.banksMonthlyTransaction[f] = amountToNumber(
            updatedItem.banksMonthlyTransaction[f],
          );
        });
        updatedItem.collectionMonthlyTransaction = {};
        updatedItem.fundsMonthlyTransaction = {};
        updatedItem.odMonthlyTransaction = {};
        updatedItem.fund = null;
        updatedItem.odDepartment = null;
      }
      if (updatedItem.section === 'COLLECTIONS') {
        collectionSection.forEach((f) => {
          updatedItem.collectionMonthlyTransaction[f] = amountToNumber(
            updatedItem.collectionMonthlyTransaction[f],
          );
        });
        updatedItem.banksMonthlyTransaction = {};
        updatedItem.odMonthlyTransaction = {};
        updatedItem.fundsMonthlyTransaction = {};
        updatedItem.bank = null;
        updatedItem.odDepartment = null;
      }
      if (updatedItem.section === 'FUNDS') {
        fundSection.forEach((f) => {
          updatedItem.fundsMonthlyTransaction[f] = amountToNumber(
            updatedItem.fundsMonthlyTransaction[f],
          );
        });
        updatedItem.banksMonthlyTransaction = {};
        updatedItem.collectionMonthlyTransaction = {};
        updatedItem.odMonthlyTransaction = {};
        updatedItem.bank = null;
        updatedItem.odDepartment = null;
      }
      if (updatedItem.section === 'OFFICIALDEPOSITORY') {
        odSection.forEach((f) => {
          updatedItem.odMonthlyTransaction[f] = amountToNumber(
            updatedItem.odMonthlyTransaction[f],
          );
        });
        updatedItem.banksMonthlyTransaction = {};
        updatedItem.collectionMonthlyTransaction = {};
        updatedItem.fundsMonthlyTransaction = {};
        updatedItem.bank = null;
        updatedItem.odDepartment = null;
      }
      if (updatedItem.bank) {
        updatedItem.bank = extractId(updatedItem.bank);
      }
      if (updatedItem.fund) {
        updatedItem.fund = extractId(updatedItem.fund);
      }
      if (updatedItem.odDepartment) {
        updatedItem.odDepartment = extractId(updatedItem.odDepartment);
      }
      return updatedItem;
    },
    async upsrtTransaction(item) {
      const { baseCrudKey } = this;
      const updatedItem = this.saveableEdits(item);
      const response = await this.upsertSaiMonthlyTransaction({ item: updatedItem, baseCrudKey });
      return response;
    },
    searchBySectionCriteria() {
      this.settingHeaders();
      this.handleLoadCRUDList();
    },
    handleLoadCRUDList() {
      const criteria = this.criteriaState;
      const { baseCrudKey } = this;
      this.loadSaiMonthlyTransaction({
        criteria,
        baseCrudKey,
      });
    },
    settingHeaders() {
      this.setHeaders(headersByType[this.criteriaState.section]);
    },
    newItemDialog() {
      const itemId = getIdFromList(this, this.saiMonthlySummaryBaseCrudKey);
      let newItem = {};
      newItem = {
        fiscalYear: this.saiMonthlySummaryCriteriaState.fiscalYear
          || toFiscalYear(new Date(this.todaysDate)),
        section: 'BANKS',
        bank: null,
        banksMonthlyTransaction: {},
        saiMonthlySummary: itemId,
      };
      if (this.criteriaState.section !== null
      && this.criteriaState.section !== undefined) {
        newItem.section = this.criteriaState.section;
      }
      return newItem;
    },
  },
};
</script>
<style>
</style>
